import { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ActivityType, AnalysisReport, Household } from "@lifelog/shared";
import { Trash2 } from "lucide-react";
import { api, ApiError, isOfflineError } from "../lib/api.js";
import { relativeTime } from "../lib/time.js";
import { AnalyzePane } from "./AnalyzePane.js";
import { ConfirmDialog } from "./ModalShell.js";
import { ListSkeleton } from "./Skeleton.js";

type ReportMeta = AnalysisReport;
type ReportFull = AnalysisReport & { markdown: string };

export function ReportsPane({ household }: { household: Household }) {
  const [types, setTypes] = useState<ActivityType[]>([]);
  const [reports, setReports] = useState<ReportMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReportFull | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ReportMeta | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [typesRes, reportsRes] = await Promise.all([
        api<{ types: ActivityType[] }>("/activity-types"),
        api<{ reports: ReportMeta[] }>("/reports"),
      ]);
      setTypes(typesRes.types.filter((t) => !t.archived));
      setReports(reportsRes.reports);
    } catch (err) {
      if (isOfflineError(err)) setError("You're offline. Try again when you're back online.");
      else if (!(err instanceof ApiError && err.status === 401)) {
        setError(err instanceof Error ? err.message : "Could not load reports.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, household.householdId]);

  async function openReport(meta: ReportMeta) {
    setLoadingId(meta.reportId);
    setError(null);
    try {
      const full = await api<ReportFull>(`/reports/${encodeURIComponent(meta.reportId)}`);
      setSelected(full);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load that report.");
    } finally {
      setLoadingId(null);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api(`/reports/${encodeURIComponent(pendingDelete.reportId)}`, { method: "DELETE" });
      setReports((prev) => prev.filter((r) => r.reportId !== pendingDelete.reportId));
      if (selected?.reportId === pendingDelete.reportId) setSelected(null);
      setPendingDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete that report.");
    } finally {
      setDeleting(false);
    }
  }

  async function copy() {
    if (!selected?.markdown) return;
    await navigator.clipboard.writeText(selected.markdown);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  if (loading) return <ListSkeleton rows={5} />;

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-4">
      <div>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Reports</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          On-demand analysis of {household.name}. Saved reports stay here.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">{error}</div>
      )}

      <AnalyzePane
        types={types}
        onCreated={(report) => {
          setReports((prev) => [report, ...prev.filter((r) => r.reportId !== report.reportId)]);
          setSelected(report);
        }}
      />

      <section>
        <h2 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">Saved reports</h2>
        {reports.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No saved reports yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
            {reports.map((r) => (
              <li key={r.reportId} className="flex items-center gap-2 px-3 py-2">
                <button
                  type="button"
                  onClick={() => void openReport(r)}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{r.title}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {relativeTime(r.createdAt)} · last {r.rangeDays}d
                    {loadingId === r.reportId ? " · loading…" : ""}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setPendingDelete(r)}
                  className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-800"
                  aria-label={`Delete ${r.title}`}
                >
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selected && (
        <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <div className="mb-2 flex items-start justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{selected.title}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(selected.createdAt).toLocaleString()} · {selected.inputTokens.toLocaleString()} in /{" "}
                {selected.outputTokens.toLocaleString()} out
              </p>
            </div>
            <button
              type="button"
              onClick={() => void copy()}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300"
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className="prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed text-gray-800 dark:text-gray-200">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{selected.markdown}</ReactMarkdown>
          </div>
        </section>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="Delete this report?"
          message="The saved markdown is removed. This cannot be undone."
          confirmLabel="Delete"
          destructive
          busy={deleting}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
